import React from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';

import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { CheckoutLoyaltySummary } from '@/components/loyalty/CheckoutLoyaltySummary';
import { setOrderNotes as persistOrderNotes } from '@/lib/orderNotesStorage';

export default function CheckoutCustomerSection({
  customerIntro,
  customerName,
  setCustomerName,
  customerPhone,
  setCustomerPhone,
  customerEmail,
  setCustomerEmail,
  orderNotes,
  setOrderNotes,
  businessId,
  phoneOk,
  referralInfoLoading,
  referralInfo,
  checkoutReferralCode,
  setCheckoutReferralCode,
}) {
  const { t } = useTranslation();
  return (
    <Card className="p-6">
      <p className="text-sm sm:text-base text-muted-foreground mb-4 leading-relaxed">{customerIntro}</p>
      <h2 className="text-xl font-semibold mb-4">{t('checkout.contactTitle')}</h2>
      <div className="space-y-4">
        <div>
          <Label htmlFor="customer-name">{t('checkout.name')}</Label>
          <Input
            id="customer-name"
            value={customerName}
            onChange={(e) => setCustomerName(e.target.value)}
            placeholder={t('checkout.namePlaceholder')}
            className="mt-2"
          />
        </div>
        <div>
          <Label htmlFor="customer-phone">{t('checkout.phone')}</Label>
          <Input
            id="customer-phone"
            type="tel"
            value={customerPhone}
            onChange={(e) => setCustomerPhone(e.target.value)}
            placeholder="+56 9 1234 5678"
            className="mt-2"
          />
          <p className="text-sm text-muted-foreground mt-1">{t('checkout.phoneHint')}</p>
          {businessId ? (
            <CheckoutLoyaltySummary businessId={businessId} phone={customerPhone} className="mt-3" />
          ) : null}
        </div>
        <div>
          <Label htmlFor="customer-email">{t('checkout.email')}</Label>
          <Input
            id="customer-email"
            type="email"
            value={customerEmail}
            onChange={(e) => setCustomerEmail(e.target.value)}
            placeholder={t('checkout.emailPlaceholder')}
            className="mt-2"
          />
        </div>
        <div>
          <Label htmlFor="order-notes">{t('checkout.notes')}</Label>
          <Textarea
            id="order-notes"
            value={orderNotes}
            onChange={(e) => {
              const v = e.target.value;
              setOrderNotes(v);
              persistOrderNotes(businessId, v);
            }}
            placeholder={t('checkout.notesPlaceholder')}
            className="mt-2 min-h-[88px]"
            rows={3}
          />
          <p className="text-sm text-muted-foreground mt-1">{t('checkout.notesHint')}</p>
        </div>

        {phoneOk ? (
          <div className="pt-2 border-t border-border/80">
            {referralInfoLoading ? (
              <p className="text-sm text-muted-foreground flex items-center gap-2">
                <Loader2 className="w-4 h-4 animate-spin shrink-0" />
                {t('checkout.referralChecking')}
              </p>
            ) : referralInfo?.referred_by ? (
              <p className="text-sm text-muted-foreground">{t('checkout.referralApplied')}</p>
            ) : (
              <div className="space-y-2">
                <Label htmlFor="checkout-referral-code">{t('checkout.referralLabel')}</Label>
                <Input
                  id="checkout-referral-code"
                  value={checkoutReferralCode}
                  onChange={(e) =>
                    setCheckoutReferralCode(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ''))
                  }
                  placeholder="Ej: ABC123"
                  className="mt-0 font-mono tracking-wide"
                  maxLength={6}
                  autoComplete="off"
                  data-testid="checkout-referral-code"
                />
                <p className="text-xs text-muted-foreground">
                  Si un amigo te compartió su código, ingrésalo antes de enviar el pedido.
                </p>
              </div>
            )}
          </div>
        ) : null}
      </div>
    </Card>
  );
}
